import { useState, useMemo } from 'react';
import {
  BarChart, Bar, AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ReferenceLine
} from 'recharts';
import { TriangleAlert } from 'lucide-react';
import { getMonthName } from './utils/dateUtils';
import { getCategoryName } from './utils/categoryUtils';

// Fixed palette per category index
const CATEGORY_COLORS = [
  '#342c82', '#7c6dd8', '#059669', '#e05c5c', '#f59e0b',
  '#0ea5e9', '#768381', '#ec4899', '#14b8a6', '#f97316',
];

// Custom X-axis tick — shows warning icon if month has alerts
const WarningTick = ({ x, y, payload, warningMonths }) => {
  const hasWarning = warningMonths.has(payload.value);
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={12} textAnchor="middle" fontSize={11} fill="#374151">
        {payload.value}
      </text>
      {hasWarning && (
        <g transform="translate(-6, 16)">
          <TriangleAlert size={12} color="#dc2626" />
        </g>
      )}
    </g>
  );
};

function getCostBillAlerts(d, fmt) {
  const alerts = [];
  if ((d.AC ?? 0) > (d.ForecastCost ?? 0)) {
    alerts.push(`Cost overrun: +${fmt((d.AC - d.ForecastCost))}`);
  }
  if ((d.ForecastBill ?? 0) > (d.INV ?? 0)) {
    alerts.push(`Bill shortfall: -${fmt((d.ForecastBill - d.INV))}`);
  }
  return alerts;
}

function getHoursAlerts(d, fmtH) {
  const alerts = [];
  if ((d.ActualHours ?? 0) > (d.Hours ?? 0)) {
    alerts.push(`Hours overrun: +${fmtH((d.ActualHours - d.Hours))}`);
  }
  return alerts;
}

const CashFlowTooltip = ({ active, payload, fmt, fmtH, alertType }) => {
  if (!active || !payload?.length) return null;
  const dataPoint = payload[0]?.payload;
  const alerts = dataPoint
    ? alertType === 'hours'
      ? getHoursAlerts(dataPoint, fmtH)
      : getCostBillAlerts(dataPoint, fmt)
    : [];
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 12px', fontSize: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{dataPoint?.month}</div>
      {payload.map((entry) => (
        <div key={entry.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '1px 0' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: entry.color, flexShrink: 0 }} />
          <span style={{ color: '#5e5e5e' }}>{entry.name}:</span>
          <span style={{ fontWeight: 500 }}>
            {alertType === 'hours' ? fmtH(entry.value) : fmt(entry.value)}
          </span>
        </div>
      ))}
      {alerts.length > 0 && (
        <div style={{ borderTop: '1px solid #fee2e2', marginTop: 6, paddingTop: 4 }}>
          {alerts.map((msg, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 500, color: '#dc2626' }}>
              <TriangleAlert size={12} color="#dc2626" /> {msg}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CashFlowCharts = ({ cashFlowData, settings }) => {
  // One hidden-set per chart
  const [hiddenChart1, setHiddenChart1] = useState({});
  const [hiddenChart2, setHiddenChart2] = useState({});
  const [hiddenChart3, setHiddenChart3] = useState({});
  const [hiddenChart4, setHiddenChart4] = useState({});

  // All unique categories present in data
  const allCategoryIds = useMemo(() => [...new Set(
    cashFlowData.flatMap(cf => cf.lines?.map(l => l.category) || [])
  )].filter(cat => cat !== null && cat !== undefined).sort(), [cashFlowData]);

  // Selected categories — default = all selected
  const [selectedCategories, setSelectedCategories] = useState(() => new Set(allCategoryIds));

  const toggleCategory = (catId) => {
    setSelectedCategories(prev => {
      const next = new Set(prev);
      if (next.has(catId)) {
        if (next.size === 1) return prev; // prevent deselecting all
        next.delete(catId);
      } else {
        next.add(catId);
      }
      return next;
    });
  };

  const selectAll = () => setSelectedCategories(new Set(allCategoryIds));
  const clearAll  = () => setSelectedCategories(new Set([allCategoryIds[0]])); // keep at least one

  // Filter lines by selected categories
  const filteredCashFlowData = useMemo(() => cashFlowData.map(cf => ({
    ...cf,
    lines: cf.lines?.filter(l => selectedCategories.has(l.category)) || [],
  })), [cashFlowData, selectedCategories]);

  const makeToggle = (setter) => (e) => {
    const key = e.dataKey;
    setter(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const legendStyle = (hidden, key) => ({
    cursor: 'pointer',
    opacity: hidden[key] ? 0.35 : 1,
  });

  const currency = settings?.currencySymbol || '$';

  const monthlyData = filteredCashFlowData.map(cf => {
    const forecastCost = cf.lines.reduce((s, l) => s + (l.forecastTotalCost || 0), 0);
    const forecastBill = cf.lines.reduce((s, l) => s + (l.forecastTotalBill || 0), 0);
    const totalInv     = cf.lines.reduce((s, l) => s + (l.inv  || 0), 0);
    const totalAc      = cf.lines.reduce((s, l) => s + (l.ac   || 0), 0);
    return {
      month:        getMonthName(cf.month).substring(0, 3),
      INV:          totalInv,
      AC:           totalAc,
      ForecastCost: forecastCost,
      ForecastBill: forecastBill,
      GP:           totalInv > 0 ? parseFloat(((totalInv - totalAc) / totalInv * 100).toFixed(2)) : 0,
      Hours:        cf.lines.reduce((s, l) => s + (l.forecastHours || 0), 0),
      ActualHours:  cf.lines.reduce((s, l) => s + (l.actualHours || 0), 0),
      hasWarning:   forecastCost < totalAc || forecastBill > totalInv,
      hasHoursWarning: cf.lines.reduce((s, l) => s + (l.actualHours || 0), 0) > cf.lines.reduce((s, l) => s + (l.forecastHours || 0), 0),
    };
  });

  const warningMonths = new Set(monthlyData.filter(m => m.hasWarning).map(m => m.month));
  const hoursWarningMonths = new Set(monthlyData.filter(m => m.hasHoursWarning).map(m => m.month));

  const filteredCategoryIds = allCategoryIds.filter(id => selectedCategories.has(id)).sort();

  const categoryData = filteredCashFlowData.map(cf => {
    const entry = { month: getMonthName(cf.month).substring(0, 3) };
    filteredCategoryIds.forEach(catId => {
      entry[`cat_${catId}`] = cf.lines
        .filter(l => l.category === catId)
        .reduce((s, l) => s + (l.forecastTotalCost || 0), 0);
    });
    return entry;
  });

  const fmt  = (v) => `${currency}${v >= 1000 ? `${(v / 1000).toFixed(2)}k` : Number(v).toFixed(2)}`;
  const fmtH = (v) => `${Number(v).toFixed(2)}h`;

  return (
    <div className="cashflow-charts-wrapper">

      {/* Category filter bar */}
      <div className="cashflow-category-filter">
        <span className="cashflow-filter-label">Filter by category:</span>
        <button className={`cashflow-filter-btn cashflow-filter-all ${selectedCategories?.size === allCategoryIds?.length && ' active'}`} onClick={selectAll}>All</button>
        {allCategoryIds.map((catId, i) => (
          <button
            key={catId}
            className={`cashflow-filter-btn ${selectedCategories.has(catId) ? 'active' : ''}`}
            style={{ '--cat-color': CATEGORY_COLORS[i % CATEGORY_COLORS.length] }}
            onClick={() => toggleCategory(catId)}
          >
            <span className="cashflow-filter-dot" />
            {getCategoryName(catId)}
          </button>
        ))}
      </div>

      <div className="cashflow-charts-container">

        {/* Chart 1: AC vs Forecast Cost + INV vs Forecast Bill — 4-bar grouped */}
        <div className="cashflow-chart-card">
          <h4 className="cashflow-chart-title">
            INV / Forecast Bill &amp; AC / Forecast Cost
            <span className="cashflow-chart-warning-legend"></span>
          </h4>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={monthlyData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="month"
                tick={<WarningTick warningMonths={warningMonths} />}
                interval={0}
                height={36}
              />
              <YAxis tickFormatter={fmt} tick={{ fontSize: 11 }} />
              <Tooltip content={<CashFlowTooltip fmt={fmt} fmtH={fmtH} alertType="cost" />} itemSorter='index' />
              <Legend
                itemSorter='index'
                wrapperStyle={{ fontSize: 11 }}
                onClick={makeToggle(setHiddenChart1)}
                formatter={(value, entry) => (
                  <span style={legendStyle(hiddenChart1, entry.dataKey)}>{value}</span>
                )}
              />
              
              <Bar index={0} dataKey="ForecastCost" name="Forecast Cost" fill="#e05c5c" radius={[2,2,0,0]} hide={!!hiddenChart1['ForecastCost']} />
              <Bar index={1} dataKey="AC"           name="AC"            fill="#8B1E3F" radius={[2,2,0,0]} hide={!!hiddenChart1['AC']} />
              <Bar index={2} dataKey="ForecastBill" name="Forecast Bill" fill="#D4A017" radius={[2,2,0,0]} hide={!!hiddenChart1['ForecastBill']} />
              <Bar index={3} dataKey="INV"          name="INV"           fill="#008080" radius={[2,2,0,0]} hide={!!hiddenChart1['INV']} />

            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Chart 2: Cost by Category — Stacked Bar */}
        <div className="cashflow-chart-card">
          <h4 className="cashflow-chart-title">Forecast Cost by Category</h4>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={categoryData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} height={35}/>
              <YAxis tickFormatter={fmt} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v, name) => [fmt(v), name]} itemSorter='key' />
              <Legend
                itemSorter='key'
                wrapperStyle={{ fontSize: 11 }}
                onClick={makeToggle(setHiddenChart2)}
                formatter={(value, entry) => (
                  <span style={legendStyle(hiddenChart2, entry.dataKey)}>{value}</span>
                )}
              />
              {filteredCategoryIds.map((catId, i) => (
                <Bar
                  key={catId}
                  dataKey={`cat_${catId}`}
                  name={getCategoryName(catId)}
                  stackId="cat"
                  fill={CATEGORY_COLORS[allCategoryIds.indexOf(catId) % CATEGORY_COLORS.length]}
                  radius={i === filteredCategoryIds.length - 1 ? [2,2,0,0] : [0,0,0,0]}
                  hide={!!hiddenChart2[`cat_${catId}`]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Chart 3: GP % per Month — Line */}
        <div className="cashflow-chart-card">
          <h4 className="cashflow-chart-title">Gross Profit %</h4>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={monthlyData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => `${v}%`} />
              <ReferenceLine y={0} stroke="#9ca3af" strokeDasharray="4 4" />
              <Legend
                wrapperStyle={{ fontSize: 11 }}
                onClick={makeToggle(setHiddenChart3)}
                formatter={(value, entry) => (
                  <span style={legendStyle(hiddenChart3, entry.dataKey)}>{value}</span>
                )}
              />
              <Line type="monotone" dataKey="GP" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} name="GP %" hide={!!hiddenChart3['GP']} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Chart 4: Forecast vs Actual Hours by Month — Area Chart */}
        <div className="cashflow-chart-card">
          <h4 className="cashflow-chart-title">Forecast vs Actual Hours</h4>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={monthlyData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <defs>
                <linearGradient id="hoursGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#342c82" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#342c82" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="actualHoursGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#a78bfa" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#a78bfa" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={<WarningTick warningMonths={hoursWarningMonths} />} interval={0} height={36} />
              <YAxis tickFormatter={fmtH} tick={{ fontSize: 11 }} />
              <Tooltip content={<CashFlowTooltip fmt={fmt} fmtH={fmtH} alertType="hours" />} />
              <Legend
                wrapperStyle={{ fontSize: 11 }}
                onClick={makeToggle(setHiddenChart4)}
                formatter={(value, entry) => (
                  <span style={legendStyle(hiddenChart4, entry.dataKey)}>{value}</span>
                )}
              />
              <Area
                type="monotone"
                dataKey="Hours"
                name="Forecast Hours"
                stroke="#342c82"
                strokeWidth={2}
                fill="url(#hoursGradient)"
                dot={{ r: 3, fill: '#342c82' }}
                hide={!!hiddenChart4['Hours']}
              />
              <Area
                type="monotone"
                dataKey="ActualHours"
                name="Actual Hours"
                stroke="#a78bfa"
                strokeWidth={2}
                fill="url(#actualHoursGradient)"
                dot={{ r: 3, fill: '#a78bfa' }}
                hide={!!hiddenChart4['ActualHours']}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

      </div>
    </div>
  );
};

export default CashFlowCharts;
